import { Component, inject, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';

import { PropertyType } from '@shared/enums/property-type.enum';
import { PropertyTypeDetail } from '@shared/enums/property-type-detail.enum';
import { getKeyFromEnumByValue, getOptionsFromEnum } from '@shared/utils/forms';

import { ButtonComponent } from '@shared/components/button';
import { SectionLockDirective } from '@shared/directives/section-lock.directive';
import { SectionLockAlertComponent } from '@shared/dasboard-sections/components/section-lock-alert/section-lock-alert.component';
import { SectionLockedPipe } from '@shared/pipes/section-locked.pipe';
import { FormErrorService } from '@shared/components/form-error-modal/form-error.service';
import { CloseModalDirective } from '@shared/directives/modals';

import { EstateFormService } from './estate-form.service';
import { EstateFormDialogData } from './estate-form.interface';
import {
  EstateCommonFormControlNames as EstateCommonFormControlNames,
  EstateFormEnum,
  EstatePersonalPropertyFormControlNames,
  EstateRealPropertyFormControlNames,
} from './estate-form.enum';
import { filter, tap } from 'rxjs';

import { CcsInputComponent } from '../ccs-input/ccs-input.component';
import { CcsSelectComponent } from '../ccs-select/ccs-select.component';
import { CcsInputMoneyComponent } from '../ccs-input-money/ccs-input-money.component';

@Component({
  selector: 'app-estate-form',
  imports: [
    CcsInputComponent,
    CcsInputMoneyComponent,
    CcsSelectComponent,
    ButtonComponent,
    ReactiveFormsModule,
    SectionLockDirective,
    SectionLockAlertComponent,
    SectionLockedPipe,
    CloseModalDirective,
  ],
  providers: [EstateFormService],
  templateUrl: './estate-form.component.html',
})
export class EstateFormComponent implements OnInit {
  private _formService = inject(EstateFormService);
  private _dialogRef = inject(DialogRef);
  private _formErrorService = inject(FormErrorService);

  private _dialogData: EstateFormDialogData = inject(DIALOG_DATA);

  protected readonly section = this._dialogData.section;
  protected readonly estateRow = this._dialogData.estate;

  protected readonly propertyTypes = getOptionsFromEnum(PropertyType);
  protected readonly propertyTypeDetails =
    getOptionsFromEnum(PropertyTypeDetail);
  protected readonly realPropertyTypeOption = getKeyFromEnumByValue(
    PropertyType,
    'REAL_PROPERTY',
  );

  protected estateForm = this._formService.form;

  ngOnInit(): void {
    if (this.estateRow) {
      this._formService.refreshFormData(this.estateRow);
    } else {
      this.estateForm.reset();
    }

    this.estateForm.controls[EstateFormEnum.TYPE_OF_PROPERTY]?.valueChanges
      .pipe(
        filter((value) => !!value),
        tap((value) => this._formService.handleTypeChange(value)),
      )
      .subscribe();
  }

  protected submitForm(): void {
    if (this.estateForm.valid) {
      const data = this._formService.normalizeFormData();
      this._dialogRef.close({
        ...data,
        id: this.estateRow?.id,
      });
    } else {
      this._formErrorService.displayErrorModal(this.estateForm);
    }
  }

  protected readonly estateCommonFormControlNames =
    EstateCommonFormControlNames;
  protected readonly estatePersonalPropertyFormControlNames =
    EstatePersonalPropertyFormControlNames;
  protected readonly estateRealPropertyFormControlNames =
    EstateRealPropertyFormControlNames;

  protected readonly estateFormEnum = EstateFormEnum;

  protected get isRealProperty() {
    return (
      this.estateForm.controls[this.estateFormEnum.TYPE_OF_PROPERTY].value ===
      this.realPropertyTypeOption
    );
  }

  protected get descriptionLabel() {
    return this.isRealProperty
      ? this.estateRealPropertyFormControlNames[this.estateFormEnum.DESCRIPTION]
      : this.estatePersonalPropertyFormControlNames[
          this.estateFormEnum.DESCRIPTION
        ];
  }
}
